<script lang="ts">
    import {getCurrentLanguage, getLogger} from 'src/core';
    import type {FormControl} from '../../forms/form-control';
    import {FormControlBuilder} from '../../forms/form-control';
    import {formControls} from '../../forms/use.form-controls';
    import type {ValueEditorSize} from '../../../types';
    import {style} from 'svelte-forms';
    import {getContext, onDestroy} from 'svelte';
    import {validatorFactory} from './utils';
    import {stepValidator} from '../../shared/validators';
    import type {NumberValueEditorLocalizations, NumberValueEditorOptions, NumberValueEditorValidations} from './types';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {touched} from '../../shared/use.touched';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import type {FormControlOptions} from '../../forms/types';
    import Description from '../../description/Description.svelte';
    import {createInputDescription} from '../../description/utils';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {required} from '../../forms/use.required';
    import {throwParseErrorIfNaN} from '../_shared/editors-utils';

    export let model: number;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: NumberValueEditorOptions;
    export let validations: NumberValueEditorValidations;

    const currentLanguage = getCurrentLanguage();
    const localize: EditorsLocalizationFunction<NumberValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    onDestroy(() => {
        subscription.unsubscribe();
    })

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withParsers([throwParseErrorIfNaN])
        .withValidators([...validatorFactory(validations), stepValidator(options.step, validations.min)])
        .withOptions(formControlOptions)
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    export function getFormController(): FormControl<number> {
        return formControl;
    }
</script>

<input type="number"
       id={editorId}
       name={editorName}
       class="form-control input-{size}{options.hideSpinners ? ' hide-spinners' : ''}"

       lang={currentLanguage}
       inputmode={options.decimal ? 'decimal' : 'numeric'}

       {placeholder}
       disabled={isDisabled}
       step={options.step}
       min={validations.min}
       max={validations.max}

       use:required={validations.required}
       use:formControls={formControl}
       use:style={{field: formControl.getFieldStateAsStore()}}
       use:focusOnMount={isFocused}
       use:touched={{formControl}}
       use:ariaInvalid={formControl}
       use:ariaErrormessage={formControl}
       use:ariaDescribedby={inputDescription}

       data-main-input/>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>

<Description {inputDescription}/>

<style lang="less">
    input.hide-spinners {
        &::-webkit-outer-spin-button,
        &::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
        }

        & {
            -moz-appearance: textfield;
            appearance: textfield;
        }
    }
</style>